import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import AepPageAccessManager from '../../Components/Admin/AepPageAccessManager'
import { BASE_URL_API } from '../../References/urls'
import AuthContext from '../../store/auth-context'
import { FiCheck, FiLoader, FiSearch, FiUserPlus, FiX } from 'react-icons/fi'
import { executivesData as storedExecutives, useExecutives, getExecutiveFullName } from '../../store/ExecutiveContext'
import styles from '../Admin/AdminEachPersonnel.module.css'
import AdminNavigator from './AdminNavigator'
import { Link } from 'react-router-dom'

// Flattens varied API responses into a clean list of executive IDs
const parseExecutiveTeamIds = (payload) => {
    const collected = new Set()

    const collect = (value) => {
        if (value === null || value === undefined) {
            return
        }

        if (Array.isArray(value)) {
            value.forEach(collect)
            return
        }

        if (typeof value === 'number') {
            const asString = String(value).trim()
            if (asString) {
                collected.add(asString)
            }
            return
        }

        if (typeof value === 'string') {
            if (value.includes(',')) {
                value.split(',').map(part => part.trim()).filter(Boolean).forEach(collect)
                return
            }
            const trimmed = value.trim()
            if (trimmed) {
                collected.add(trimmed)
            }
            return
        }

        if (typeof value === 'object') {
            const idCandidates = ['userID', 'userId', 'executiveId', 'id', 'value']
            idCandidates.forEach((key) => {
                if (value[key]) {
                    collect(value[key])
                }
            })

            const nestedCandidates = ['team', 'executives', 'members', 'response', 'data', 'list', 'executiveTeam']
            nestedCandidates.forEach((key) => {
                if (value[key]) {
                    collect(value[key])
                }
            })
        }
    }

    collect(payload)
    return Array.from(collected)
}

const buildExecutiveName = (executive) => {
    if (!executive) {
        return ''
    }

    const nameParts = [executive.firstName, executive.middleName, executive.lastName].filter(Boolean)
    if (nameParts.length) {
        return nameParts.join(' ')
    }

    return executive.fullName || executive.name || executive.username || executive.userID || ''
}

const buildExecutiveInitials = (executive) => {
    const name = buildExecutiveName(executive)
    if (!name) {
        const fallback = executive?.username || executive?.userID || '?'
        return fallback.slice(0, 2).toUpperCase()
    }

    const parts = name.split(' ').filter(Boolean)
    if (!parts.length) {
        return '?'
    }

    if (parts.length === 1) {
        return parts[0].slice(0, 2).toUpperCase()
    }

    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
}

const AdminEachPersonnel = () => {
    const authCtx = useContext(AuthContext)
    const executivesContext = useExecutives()
    const hasAccessTo = authCtx.hasAccessTo ? JSON.parse(authCtx.hasAccessTo) : []
    // console.log("Access to:", hasAccessTo)
    // console.log("Executives data snapshot:", storedExecutives) 
    // below is the format of executivesData
    //[
    // {
    //     "username": "lokaciteam5168",
    //     "userID": "LOKACITEAM5168",
    //     "firstName": "Ankush",
    //     "middleName": "",
    //     "lastName": "Saxena",
    //     "isActive": "1",
    //     "isBlocked": "0"
    // }
    // ..

    const { personnelIdentifier } = useParams()
    // console.log("Personnel ID from URL:", personnelIdentifier)
    const [activeTab, setActiveTab] = useState('summary')
    const [recruiterData, setRecruiterData] = useState(null)
    const [loading, setLoading] = useState(true)
    const [isAssignDrawerOpen, setIsAssignDrawerOpen] = useState(false)
    const [selectedRecruiters, setSelectedRecruiters] = useState([])
    const [assignSearchTerm, setAssignSearchTerm] = useState('')
    const [teamLoading, setTeamLoading] = useState(false)
    const [teamError, setTeamError] = useState('')
    const [existingTeamIds, setExistingTeamIds] = useState([])
    const [isUpdatingTeam, setIsUpdatingTeam] = useState(false)

    const contextExecutives = executivesContext?.executives ?? []
    const isExecutivesLoading = executivesContext?.isLoading ?? false

    const availableExecutives = useMemo(() => {
        if (Array.isArray(contextExecutives) && contextExecutives.length) {
            return contextExecutives
        }
        return Array.isArray(storedExecutives) ? storedExecutives : []
    }, [contextExecutives])

    const sortedExecutives = useMemo(() => {
        if (!Array.isArray(availableExecutives)) {
            return []
        }

        return [...availableExecutives].sort((a, b) => {
            const aActive = a?.isActive === '1' || a?.isActive === 1 || a?.isActive === true ? 1 : 0
            const bActive = b?.isActive === '1' || b?.isActive === 1 || b?.isActive === true ? 1 : 0
            if (aActive !== bActive) {
                return bActive - aActive
            }

            const nameA = buildExecutiveName(a) || ''
            const nameB = buildExecutiveName(b) || ''
            if (nameA && nameB) {
                return nameA.localeCompare(nameB)
            }

            if (nameA) {
                return -1
            }

            if (nameB) {
                return 1
            }

            return 0
        })
    }, [availableExecutives])

    const filteredExecutives = useMemo(() => {
        if (!Array.isArray(sortedExecutives)) {
            return []
        }

        const query = assignSearchTerm.trim().toLowerCase()
        if (!query) {
            return sortedExecutives
        }

        return sortedExecutives.filter((executive) => {
            const searchable = `${buildExecutiveName(executive)} ${executive?.username ?? ''} ${executive?.userID ?? ''}`.toLowerCase()
            return searchable.includes(query)
        })
    }, [sortedExecutives, assignSearchTerm])

    const isSelectionDifferent = useMemo(() => {
        if (selectedRecruiters.length !== existingTeamIds.length) {
            return true
        }

        const existingSet = new Set(existingTeamIds)
        return selectedRecruiters.some((id) => !existingSet.has(id))
    }, [selectedRecruiters, existingTeamIds])

    useEffect(() => {
        fetchRecruiterData()
    }, [personnelIdentifier])

    const fetchRecruiterData = async () => {
        try {
            const response = await fetch(`${BASE_URL_API}/getExecutiveData`, 
            {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token ,
                    executiveIdentifier : personnelIdentifier
                }),
            })
            if(!response.ok) {
                throw new Error('Failed to fetch recruiter data')
            } else {
                const data = await response.json()
                setRecruiterData(data.response)
            }
        } catch (error) {
            console.error('Error fetching recruiter data:', error)
            setRecruiterData(mockRecruiterData)
        } finally {
            setLoading(false)
        }
    }

    // Mock data for development - This is ONE recruiter's complete profile
    const mockRecruiterData = {
        id: 1,
        name: getExecutiveFullName(personnelIdentifier),
        email: 'priya@stylelink.in',
        phone: '+91 98765 43210',
        status: 'active',
        role: 'Team Lead',
        department: 'Mumbai Region',
        joinDate: '2024-01-15',
        reportingTo: 'Navneet Chandan',
        
        // Their stats
        stats: {
            totalPlacements: 145,
            monthlyPlacements: 12,
            activeCandidates: 28,
            activeSalons: 15,
            avgResponseTime: '2.3 hrs',
            performance: 92
        },

        // Junior recruiters under them
        teamMembers: [
            {
                id: 101,
                name: 'Rahul Verma',
                role: 'Junior Recruiter',
                placements: 28,
                activeCandidates: 8,
                performance: 85,
                status: 'active',
                lastActive: '15 mins ago'
            },
            {
                id: 102,
                name: 'Anjali Patel',
                role: 'Junior Recruiter',
                placements: 12,
                activeCandidates: 3,
                performance: 78,
                status: 'active',
                lastActive: '2 hours ago'
            },
            {
                id: 103,
                name: 'Sneha Reddy',
                role: 'Recruiter',
                placements: 31,
                activeCandidates: 10,
                performance: 88,
                status: 'active',
                lastActive: '30 mins ago'
            }
        ],

        // Recent performance data
        weeklyStats: [
            { week: 'Week 1', placements: 3, calls: 45, interviews: 12 },
            { week: 'Week 2', placements: 4, calls: 52, interviews: 15 },
            { week: 'Week 3', placements: 2, calls: 38, interviews: 10 },
            { week: 'Week 4', placements: 3, calls: 48, interviews: 14 }
        ]
    }

    // Recent activities for THIS recruiter and their team
    const recentActivities = [
        { user: 'Priya Sharma', action: 'Placed candidate at Lakme Salon', time: '5 mins ago', type: 'placement' },
        { user: 'Rahul Verma', action: 'Updated candidate profile', time: '12 mins ago', type: 'update' },
        { user: 'Anjali Patel', action: 'Scheduled interview', time: '25 mins ago', type: 'interview' },
        { user: 'Sneha Reddy', action: 'Added new salon', time: '1 hour ago', type: 'salon' },
        { user: 'Priya Sharma', action: 'Verified candidate documents', time: '2 hours ago', type: 'verification' }
    ]

    // Access requests from THIS recruiter
    const pageRequests = [
        { page: 'Salary Settings', status: 'pending', time: '1 hour ago' },
        { page: 'Analytics Dashboard', status: 'approved', time: '3 hours ago' },
        { page: 'Export Data', status: 'approved', time: '1 day ago' }
    ]


    const getExecutiveTeam = async () => {
        if (!personnelIdentifier) {
            return
        }

        setTeamLoading(true)
        setTeamError('')

        try {
            const response = await fetch(`${BASE_URL_API}/getExecutiveTeam`, {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    executiveId: personnelIdentifier
                })
            })

            const data = await response.json()

            if (!response.ok) {
                throw new Error(data?.message || 'Failed to fetch assigned team')
            }

            const teamIds = parseExecutiveTeamIds(data)
            setExistingTeamIds(teamIds)
            setSelectedRecruiters(teamIds)
            console.log('Executive team data:', data)
        } catch (error) {
            console.error('Error fetching executive team:', error)
            setTeamError('Unable to load assigned team right now.')
        } finally {
            setTeamLoading(false)
        }
    }

    useEffect(() => {
        if (personnelIdentifier) {
            getExecutiveTeam()
        }
    }, [personnelIdentifier])

    const data = recruiterData

    const handleOpenAssignDrawer = () => {
        setAssignSearchTerm('')
        setSelectedRecruiters(existingTeamIds)
        setIsAssignDrawerOpen(true)
    }

    const handleCloseAssignDrawer = () => {
        if (isUpdatingTeam) {
            return
        }
        setIsAssignDrawerOpen(false)
        setAssignSearchTerm('')
    }

    const handleToggleRecruiter = (rawId) => {
        const userId = String(rawId ?? '').trim()
        if (!userId) {
            return
        }

        setSelectedRecruiters((prev) => {
            if (prev.includes(userId)) {
                return prev.filter((id) => id !== userId)
            }
            return [...prev, userId]
        })
    }

    const handleUpdateExecutiveTeam = async () => {
        if (!personnelIdentifier || !isSelectionDifferent) {
            return
        }

        const payloadTeam = Array.from(new Set(selectedRecruiters.map((id) => String(id ?? '').trim()).filter(Boolean)))

        setIsUpdatingTeam(true)
        setTeamError('')

        try {
            const response = await fetch(`${BASE_URL_API}/updateExecutiveTeam`, {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    executiveId: personnelIdentifier,
                    executiveTeam: payloadTeam,
                })
            })

            const data = await response.json()
            console.log(response)

            if (!response.ok || data?.status === 'error') {
                throw new Error(data?.message || 'Failed to update team')
            }

            setExistingTeamIds(payloadTeam)
            setSelectedRecruiters(payloadTeam)
            setIsAssignDrawerOpen(false)
            setAssignSearchTerm('')
            await fetchRecruiterData()
        } catch (error) {
            console.error('Error updating executive team:', error)
            setTeamError(error?.message || 'Unable to update team. Please try again.')
        } finally {
            setIsUpdatingTeam(false)
        }
    }


    if (loading) {
        return <div>Loading...</div>
    }



    return (
        <div className={styles.mainWrapper}>
            <AdminNavigator />
            <div className={styles.mainContainer}>
                <div className={styles.leftWrapper}>
                    {/* Recruiter Header */}
                    <div className={styles.recruiterHeader}>
                        <div className={styles.recruiterProfile}>
                            <div className={styles.avatarLarge}>
                                {data?.name?.split(' ')?.map(n => n[0]).join('')}
                            </div>
                            <div className={styles.recruiterMainInfo}>
                                <h1>{data.name}</h1>
                                <div className={styles.recruiterMeta}>
                                    <span className={styles.roleTagLarge}>{data.role}</span>
                                    <span className={styles.metaDot}>•</span>
                                    <span>{data.department}</span>
                                    <span className={styles.metaDot}>•</span>
                                    <span className={data?.isActive === "1" ? styles.statusActive : styles.statusInactive}>
                                        {data.isActive === "1" ? 'Active' : 'Inactive'}
                                    </span>
                                </div>
                                <div className={styles.contactInfo}>
                                    <span>📧 {data?.email}</span>
                                    <span>📱 {data?.phoneNumberAlllotedCandidate}</span>
                                    <span>📅 Joined {new Date(data?.joinDate).toLocaleDateString()}</span>
                                    <span>👤 Reports to: {getExecutiveFullName(data?.reportingTo)}</span>
                                </div>
                            </div>
                        </div>
                        <div className={styles.headerActions}>
                            <button className={styles.btnSecondary}>Edit Profile</button>
                            <button className={styles.btnSecondary}>Reset Password</button>
                            <button className={styles.btnSecondary}>⋮</button>
                        </div>
                    </div>

                    {/* Quick Stats */}
                    <div className={styles.statsGrid}>
                        <div className={styles.statCard}>
                            <div className={styles.statValue}>{data?.stats?.totalPlacements}</div>
                            <div className={styles.statLabel}>Total Placements</div>
                        </div>
                        <div className={styles.statCard}>
                            <div className={styles.statValue}>{data?.stats?.monthlyPlacements}</div>
                            <div className={styles.statLabel}>This Month</div>
                        </div>
                        <div className={styles.statCard}>
                            <div className={styles.statValue}>{data?.stats?.activeCandidates}</div>
                            <div className={styles.statLabel}>Active Candidates</div>
                        </div>
                        <div className={styles.statCard}>
                            <div className={styles.statValue}>{data?.stats?.activeSalons}</div>
                            <div className={styles.statLabel}>Active Salons</div>
                        </div>
                        <div className={styles.statCard}>
                            <div className={styles.statValue}>{data?.stats?.avgResponseTime}</div>
                            <div className={styles.statLabel}>Avg Response</div>
                        </div>
                        <div className={styles.statCard}>
                            <div className={styles.statValue}>{data?.stats?.performance}%</div>
                            <div className={styles.statLabel}>Performance</div>
                        </div>
                    </div>

                    <div className={styles.navigatorSmall}>
                        <div className={styles.navigatorSmallContainer}>
                            <div 
                                onClick={() => setActiveTab('summary')}
                                className={activeTab === 'summary' ? styles.eachActiveTab : styles.eachTab}
                            >
                                Summary
                            </div>
                            <div 
                                onClick={() => setActiveTab('team')}
                                className={activeTab === 'team' ? styles.eachActiveTab : styles.eachTab}
                            >
                                Team ({data.teamMembers.length})
                            </div>
                            <div 
                                onClick={() => setActiveTab('performance')}
                                className={activeTab === 'performance' ? styles.eachActiveTab : styles.eachTab}
                            >
                                Performance
                            </div>
                            <div 
                                onClick={() => setActiveTab('accessibility')}
                                className={activeTab === 'accessibility' ? styles.eachActiveTab : styles.eachTab}
                            >
                                Accessibility
                            </div>
                            <div 
                                onClick={() => setActiveTab('settings')}
                                className={activeTab === 'settings' ? styles.eachActiveTab : styles.eachTab}
                            >
                                Settings
                            </div>
                        </div>
                    </div>

                    <div className={styles.leftPageWrapper}>
                        {activeTab === 'summary' && (
                            <div className={styles.summaryView}>
                                <div className={styles.summarySection}>
                                    <h3>Weekly Performance</h3>
                                    <div className={styles.weeklyStatsTable}>
                                        <table>
                                            <thead>
                                                <tr>
                                                    <th>Period</th>
                                                    <th>Placements</th>
                                                    <th>Calls</th>
                                                    <th>Interviews</th>
                                                    <th>Conversion</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {data?.weeklyStats?.map((week, idx) => (
                                                    <tr key={idx}>
                                                        <td>{week.week}</td>
                                                        <td><strong>{week.placements}</strong></td>
                                                        <td>{week.calls}</td>
                                                        <td>{week.interviews}</td>
                                                        <td>{((week.placements / week.interviews) * 100).toFixed(1)}%</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                <div className={styles.summaryGrid}>
                                    <div className={styles.summarySection}>
                                        <h3>Recent Placements</h3>
                                        <div className={styles.placementsList}>
                                            <div className={styles.placementItem}>
                                                <div className={styles.placementName}>Anita Desai → Lakme Salon</div>
                                                <div className={styles.placementMeta}>
                                                    <span>Hair Stylist</span>
                                                    <span>₹18,000/mo</span>
                                                    <span>2 days ago</span>
                                                </div>
                                            </div>
                                            <div className={styles.placementItem}>
                                                <div className={styles.placementName}>Ravi Kumar → Naturals Salon</div>
                                                <div className={styles.placementMeta}>
                                                    <span>Makeup Artist</span>
                                                    <span>₹22,000/mo</span>
                                                    <span>5 days ago</span>
                                                </div>
                                            </div>
                                            <div className={styles.placementItem}>
                                                <div className={styles.placementName}>Meera Patel → Glow Studio</div>
                                                <div className={styles.placementMeta}>
                                                    <span>Beautician</span>
                                                    <span>₹16,000/mo</span>
                                                    <span>1 week ago</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className={styles.summarySection}>
                                        <h3>Active Pipeline</h3>
                                        <div className={styles.pipelineStats}>
                                            <div className={styles.pipelineItem}>
                                                <span>Interview Scheduled</span>
                                                <strong>8</strong>
                                            </div>
                                            <div className={styles.pipelineItem}>
                                                <span>Pending Verification</span>
                                                <strong>5</strong>
                                            </div>
                                            <div className={styles.pipelineItem}>
                                                <span>Offer Extended</span>
                                                <strong>3</strong>
                                            </div>
                                            <div className={styles.pipelineItem}>
                                                <span>In Discussion</span>
                                                <strong>12</strong>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'team' && (
                            <div className={styles.teamView}>
                                <div className={styles.teamHeader}>
                                    <h3>Junior Recruiters Under {data.name}</h3>
                                    <button 
                                        className={`${styles.btnPrimary} ${styles.assignButton}`}
                                        type="button"
                                        onClick={handleOpenAssignDrawer}
                                    >
                                        <FiUserPlus />
                                        <span>Assign Recruiter</span>
                                    </button>
                                </div>
                                <div className={styles.teamTable}>
                                    <table>
                                        <thead>
                                            <tr>
                                                <th>Name</th>
                                                <th>Role</th>
                                                <th>Placements</th>
                                                <th>Active Candidates</th>
                                                <th>Performance</th>
                                                <th>Last Active</th>
                                                <th>Status</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data?.teamMembers?.map(member => (
                                                <tr key={member.id}>
                                                    <td>
                                                        <div className={styles.recruiterInfo}>
                                                            <div className={styles.avatar}>
                                                                {member.name?.split(' ')?.map(n => n[0])?.join('')}
                                                            </div>
                                                            <span className={styles.recruiterName}>{member.name}</span>
                                                        </div>
                                                    </td>
                                                    <td><span className={styles.roleTag}>{member.role}</span></td>
                                                    <td><strong>{member.placements}</strong></td>
                                                    <td><span className={styles.activeBadge}>{member.activeCandidates}</span></td>
                                                    <td>
                                                        <div className={styles.performanceBar}>
                                                            <div 
                                                                className={styles.performanceFill}
                                                                style={{ 
                                                                    width: `${member.performance}%`,
                                                                    backgroundColor: member.performance >= 90 ? '#10b981' : 
                                                                                    member.performance >= 80 ? '#3b82f6' : '#f59e0b'
                                                                }}
                                                            />
                                                            <span className={styles.performanceText}>{member.performance}%</span>
                                                        </div>
                                                    </td>
                                                    <td><span className={styles.timeAgo}>{member.lastActive}</span></td>
                                                    <td>
                                                        <span className={member.status === 'active' ? styles.statusActive : styles.statusInactive}>
                                                            {member.status}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <div className={styles.actionButtons}>
                                                            <Link to={`/control/personnel/${member.id}`} className={styles.btnIcon} title="View">👁️</Link>
                                                            <button className={styles.btnIcon} title="Remove">❌</button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                <div className={styles.teamSummary}>
                                    <div className={styles.teamStat}>
                                        <span>Team Size</span>
                                        <strong>{data.teamMembers.length}</strong>
                                    </div>
                                    <div className={styles.teamStat}>
                                        <span>Team Placements</span>
                                        <strong>{data?.teamMembers?.reduce((sum, m) => sum + m.placements, 0)}</strong>
                                    </div>
                                    <div className={styles.teamStat}>
                                        <span>Avg Team Performance</span>
                                        <strong>
                                            {Math.round(data?.teamMembers?.reduce((sum, m) => sum + m.performance, 0) / data?.teamMembers?.length)}%
                                        </strong>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'performance' && (
                            <div className={styles.performanceView}>
                                <div className={styles.performanceGrid}>
                                    <div className={styles.perfCard}>
                                        <h4>Monthly Trend</h4>
                                        <div className={styles.trendChart}>
                                            {['Jan', 'Feb', 'Mar', 'Apr']?.map(month => (
                                                <div key={month} className={styles.trendBar}>
                                                    <div className={styles.trendValue}>12</div>
                                                    <div className={styles.trendFill} style={{ height: '80%' }}></div>
                                                    <div className={styles.trendLabel}>{month}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className={styles.perfCard}>
                                        <h4>Performance Metrics</h4>
                                        <div className={styles.metricsList}>
                                            <div className={styles.metricRow}>
                                                <span>Placement Rate</span>
                                                <strong>{data.stats.performance}%</strong>
                                            </div>
                                            <div className={styles.metricRow}>
                                                <span>Response Time</span>
                                                <strong>{data.stats.avgResponseTime}</strong>
                                            </div>
                                            <div className={styles.metricRow}>
                                                <span>Candidate Satisfaction</span>
                                                <strong>4.6/5</strong>
                                            </div>
                                            <div className={styles.metricRow}>
                                                <span>Salon Retention</span>
                                                <strong>89%</strong>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'accessibility' && (
                            <AepPageAccessManager />
                        )}

                        {activeTab === 'settings' && (
                            <div className={styles.settingsView}>
                                <div className={styles.settingsSection}>
                                    <h3>Account Settings</h3>
                                    <div className={styles.settingItem}>
                                        <label>Email Notifications</label>
                                        <input type="checkbox" className={styles.settingCheckbox} defaultChecked />
                                    </div>
                                    <div className={styles.settingItem}>
                                        <label>WhatsApp Updates</label>
                                        <input type="checkbox" className={styles.settingCheckbox} defaultChecked />
                                    </div>
                                    <div className={styles.settingItem}>
                                        <label>Auto-assign New Candidates</label>
                                        <input type="checkbox" className={styles.settingCheckbox} />
                                    </div>
                                </div>

                                <div className={styles.settingsSection}>
                                    <h3>Permissions</h3>
                                    <div className={styles.settingItem}>
                                        <label>Can Edit Salaries</label>
                                        <input type="checkbox" className={styles.settingCheckbox} />
                                    </div>
                                    <div className={styles.settingItem}>
                                        <label>Can Delete Candidates</label>
                                        <input type="checkbox" className={styles.settingCheckbox} />
                                    </div>
                                    <div className={styles.settingItem}>
                                        <label>Access to Analytics</label>
                                        <input type="checkbox" className={styles.settingCheckbox} defaultChecked />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className={styles.rightWrapper}>
                    <div className={styles.rightSection}>
                        <h3 className={styles.rightTitle}>Recent Activities</h3>
                        <div className={styles.activityList}>
                            {recentActivities.map((activity, idx) => (
                                <div key={idx} className={styles.activityItem}>
                                    <div className={styles.activityIcon}>
                                        {activity.type === 'placement' ? '✅' : 
                                         activity.type === 'interview' ? '📅' :
                                         activity.type === 'salon' ? '🏪' : '📝'}
                                    </div>
                                    <div className={styles.activityContent}>
                                        <div className={styles.activityUser}>{activity.user}</div>
                                        <div className={styles.activityAction}>{activity.action}</div>
                                        <div className={styles.activityTime}>{activity.time}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className={styles.rightSection}>
                        <h3 className={styles.rightTitle}>Page Requests</h3>
                        <div className={styles.requestList}>
                            {pageRequests.map((request, idx) => (
                                <div key={idx} className={styles.requestItem}>
                                    <div className={styles.requestPage}>{request.page}</div>
                                    <div className={styles.requestMeta}>
                                        <span className={
                                            request.status === 'pending' ? styles.statusPending :
                                            request.status === 'approved' ? styles.statusApproved :
                                            styles.statusRejected
                                        }>
                                            {request.status}
                                        </span>
                                        <span className={styles.requestTime}>{request.time}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className={styles.rightSection}>
                        <h3 className={styles.rightTitle}>Quick Actions</h3>
                        <div className={styles.quickActions}>
                            <button className={styles.quickBtn}>📊 Export Data</button>
                            <button className={styles.quickBtn}>📧 Send Message</button>
                            <button className={styles.quickBtn}>🔒 Suspend Account</button>
                        </div>
                    </div>
                </div>
            </div>

            <div
                className={`${styles.assignOverlay} ${isAssignDrawerOpen ? styles.assignOverlayVisible : ''}`}
                onClick={handleCloseAssignDrawer}
            />

            <aside className={`${styles.assignDrawer} ${isAssignDrawerOpen ? styles.assignDrawerOpen : ''}`}>
                <div className={styles.assignDrawerHeader}>
                    <div className={styles.assignDrawerTitleWrap}>
                        <div className={styles.assignDrawerIcon}>
                            <FiUserPlus />
                        </div>
                        <div>
                            <div className={styles.assignDrawerTitle}>Assign Recruiters</div>
                            <div className={styles.assignDrawerSubtitle}>Select team members to collaborate on this executive</div>
                        </div>
                    </div>
                    <div className={styles.assignHeaderRight}>
                        <span className={styles.assignCountBadge}>{selectedRecruiters.length}</span>
                        <button
                            type="button"
                            className={styles.assignCloseBtn}
                            onClick={handleCloseAssignDrawer}
                            aria-label="Close assign recruiter panel"
                            disabled={isUpdatingTeam}
                        >
                            <FiX />
                        </button>
                    </div>
                </div>

                <div className={styles.assignSearch}>
                    <div className={styles.assignSearchField}>
                        <FiSearch className={styles.assignSearchIcon} />
                        <input
                            type="text"
                            value={assignSearchTerm}
                            onChange={(event) => setAssignSearchTerm(event.target.value)}
                            placeholder="Search recruiters by name or ID"
                            className={styles.assignSearchInput}
                            disabled={isUpdatingTeam}
                        />
                    </div>
                </div>

                <div className={styles.assignList}>
                    {teamLoading || isExecutivesLoading ? (
                        <div className={styles.assignLoader}>
                            <FiLoader className={styles.assignLoaderIcon} />
                            <span>Loading recruiters...</span>
                        </div>
                    ) : filteredExecutives.length ? (
                        filteredExecutives.map((executive) => {
                            const candidateId = String(executive?.userID ?? executive?.userId ?? executive?.username ?? '').trim()
                            if (!candidateId) {
                                return null
                            }

                            const isSelected = selectedRecruiters.includes(candidateId)
                            const isActive = executive?.isActive === '1' || executive?.isActive === 1 || executive?.isActive === true

                            return (
                                <button
                                    key={candidateId}
                                    type="button"
                                    className={`${styles.assignListItem} ${isSelected ? styles.assignListItemSelected : ''}`}
                                    onClick={() => handleToggleRecruiter(candidateId)}
                                    disabled={isUpdatingTeam}
                                >
                                    <div className={styles.assignAvatar}>{buildExecutiveInitials(executive)}</div>
                                    <div className={styles.assignListContent}>
                                        <div className={styles.assignListPrimary}>{buildExecutiveName(executive)}</div>
                                        <div className={styles.assignListSecondary}>
                                            <span>{executive?.username || executive?.userID}</span>
                                            {!isActive ? (
                                                <span className={styles.statusInactive}>Inactive</span>
                                            ) : null}
                                        </div>
                                    </div>
                                    <div className={styles.assignCheckbox}>{isSelected ? <FiCheck /> : null}</div>
                                </button>
                            )
                        })
                    ) : (
                        <div className={styles.assignEmpty}>
                            <FiUserPlus />
                            <span>No recruiters found</span>
                            <small>Try searching with a different keyword.</small>
                        </div>
                    )}
                </div>

                <div className={styles.assignFooter}>
                    {teamError ? <div className={styles.assignError}>{teamError}</div> : null}
                    <div className={styles.assignFooterMeta}>
                        <span>{selectedRecruiters.length} recruiter{selectedRecruiters.length === 1 ? '' : 's'} selected</span>
                    </div>
                    <button
                        type="button"
                        className={styles.assignUpdateBtn}
                        onClick={handleUpdateExecutiveTeam}
                        disabled={!isSelectionDifferent || isUpdatingTeam}
                    >
                        {isUpdatingTeam ? (
                            <>
                                <FiLoader className={styles.assignLoaderIcon} />
                                <span>Updating</span>
                            </>
                        ) : (
                            'Update Team'
                        )}
                    </button>
                </div>
            </aside>
        </div>
    )
}

export default AdminEachPersonnel